import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PptxGenJS from 'pptxgenjs';
import { formatDate, formatDateRange, formatNumber } from './utils';

// ========== PDF EXPORT ==========

const BRAND_RED_RGB = [220, 38, 38];
const DARK_BG_RGB = [15, 15, 15];
const CARD_BG_RGB = [25, 28, 36];
const TEXT_WHITE_RGB = [255, 255, 255];
const TEXT_MUTED_RGB = [161, 161, 170];
const SUCCESS_RGB = [16, 185, 129];

export function getEffectiveMetrics(report) {
    const m = report.metrics || {};
    const li = report.linkedin || {};
    const em = report.email || {};
    return {
        target_accounts: m.target_accounts || li.target_accounts || 0,
        total_contacts: m.total_contacts || li.target_contacts || 0,
        total_reachouts: m.total_reachouts || ((li.reachouts_sent || 0) + (em.emails_sent || 0)),
        responses: m.responses || ((li.responses || 0) + (em.replies || 0)),
        positive_responses: m.positive_responses || ((li.positive_responses || 0) + (em.positive_replies || 0)),
        rejects: m.rejects || li.rejections || 0,
        future_leads: m.future_leads || li.in_progress || 0,
        mqls: m.mqls || ((li.mqls || 0) + (em.mqls || 0)),
        qualified_leads: m.qualified_leads || ((li.qualified_leads || 0) + (em.qualified_leads || 0)),
    };
}

function addPdfPageCheck(pdf, y, needed = 30) {
    if (y + needed > 270) {
        pdf.addPage();
        return 20;
    }
    return y;
}

function addPdfSectionTitle(pdf, title, y, pageWidth) {
    y = addPdfPageCheck(pdf, y, 20);
    pdf.setTextColor(...BRAND_RED_RGB);
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, 15, y);
    return y + 3;
}

function addPdfFooters(pdf) {
    const pageCount = pdf.internal.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFillColor(...DARK_BG_RGB);
        pdf.rect(0, 285, pageWidth, 12, 'F');
        pdf.setTextColor(...BRAND_RED_RGB);
        pdf.setFontSize(7);
        pdf.text('NovantPro ABM Dashboard', 15, 292);
        pdf.setTextColor(...TEXT_MUTED_RGB);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 30, 292);
    }
}

export function exportToPDF(report, reportType) {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const km = getEffectiveMetrics(report);

    // ── Header ──
    pdf.setFillColor(...DARK_BG_RGB);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setTextColor(...BRAND_RED_RGB);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text('NOVANTPRO', 15, 22);
    pdf.setTextColor(...TEXT_WHITE_RGB);
    pdf.setFontSize(10);
    pdf.text(`${reportType.toUpperCase()} REPORT`, pageWidth - 55, 15);
    pdf.setTextColor(...TEXT_MUTED_RGB);
    pdf.setFontSize(8);
    pdf.text(formatDateRange(report.period_start, report.period_end), pageWidth - 55, 23);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 55, 30);

    // ── 1. KEY METRICS ──
    let y = 45;
    y = addPdfSectionTitle(pdf, 'KEY METRICS', y, pageWidth);

    autoTable(pdf, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
            ['Target Accounts', String(km.target_accounts)],
            ['Total Contacts', String(km.total_contacts)],
            ['Total Reachouts', String(km.total_reachouts)],
            ['Responses', String(km.responses)],
            ['Positive Responses', String(km.positive_responses)],
            ['Rejects', String(km.rejects)],
            ['Future Leads', String(km.future_leads)],
            ['MQLs', String(km.mqls)],
            ['Qualified Leads (SQLs)', String(km.qualified_leads)],
        ],
        theme: 'grid',
        headStyles: { fillColor: BRAND_RED_RGB, textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { textColor: [50, 50, 50], fontSize: 9 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 15, right: 15 },
        tableWidth: pageWidth - 30,
    });
    y = pdf.lastAutoTable.finalY + 12;

    // ── 2. GOAL vs ACTUAL ──
    const goals = report.goals || {};
    const hasGoals = goals.target_mqls > 0 || goals.target_qualified_leads > 0 || goals.total_reachouts > 0 || goals.responses > 0;
    if (hasGoals) {
        y = addPdfSectionTitle(pdf, 'GOAL vs ACTUAL', y, pageWidth);
        const goalRows = [];
        if (goals.target_mqls > 0) {
            const pct = (km.mqls / goals.target_mqls * 100).toFixed(1);
            goalRows.push(['MQLs', String(km.mqls), String(goals.target_mqls), `${pct}%`]);
        }
        if (goals.target_qualified_leads > 0) {
            const pct = (km.qualified_leads / goals.target_qualified_leads * 100).toFixed(1);
            goalRows.push(['SQLs', String(km.qualified_leads), String(goals.target_qualified_leads), `${pct}%`]);
        }
        if (goals.total_reachouts > 0) {
            const pct = (km.total_reachouts / goals.total_reachouts * 100).toFixed(1);
            goalRows.push(['Reachouts', String(km.total_reachouts), String(goals.total_reachouts), `${pct}%`]);
        }
        if (goals.responses > 0) {
            const pct = (km.responses / goals.responses * 100).toFixed(1);
            goalRows.push(['Responses', String(km.responses), String(goals.responses), `${pct}%`]);
        }

        autoTable(pdf, {
            startY: y,
            head: [['Metric', 'Actual', 'Goal', 'Achievement']],
            body: goalRows,
            theme: 'grid',
            headStyles: { fillColor: SUCCESS_RGB, textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 9 },
            bodyStyles: { textColor: [50, 50, 50], fontSize: 9 },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            margin: { left: 15, right: 15 },
            tableWidth: pageWidth - 30,
        });
        y = pdf.lastAutoTable.finalY + 12;
    }

    // ── 3. LINKEDIN - OVERALL ──
    y = addPdfPageCheck(pdf, y, 40);
    y = addPdfSectionTitle(pdf, 'LINKEDIN PERFORMANCE', y, pageWidth);

    autoTable(pdf, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
            ['Target Accounts', String(report.linkedin?.target_accounts || 0)],
            ['Target Contacts', String(report.linkedin?.target_contacts || 0)],
            ['Reachouts Sent', String(report.linkedin?.reachouts_sent || 0)],
            ['Connection Accepted', String(report.linkedin?.connection_accepted || 0)],
            ['Responses', String(report.linkedin?.responses || 0)],
            ['Positive Responses', String(report.linkedin?.positive_responses || 0)],
            ['Rejections', String(report.linkedin?.rejections || 0)],
            ['In Progress', String(report.linkedin?.in_progress || 0)],
            ['MQLs', String(report.linkedin?.mqls || 0)],
            ['Qualified Leads', String(report.linkedin?.qualified_leads || 0)],
        ],
        theme: 'grid',
        headStyles: { fillColor: [0, 119, 181], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { textColor: [50, 50, 50], fontSize: 9 },
        alternateRowStyles: { fillColor: [240, 248, 255] },
        margin: { left: 15, right: 15 },
        tableWidth: pageWidth - 30,
    });
    y = pdf.lastAutoTable.finalY + 10;

    // ── 4. LINKEDIN - PROFILE-WISE BREAKDOWN ──
    const profiles = report.linkedin?.profiles || [];
    if (profiles.length > 0) {
        y = addPdfPageCheck(pdf, y, 40);
        y = addPdfSectionTitle(pdf, 'LINKEDIN PROFILE-WISE BREAKDOWN', y, pageWidth);

        autoTable(pdf, {
            startY: y,
            head: [['Profile', 'Reachouts', 'Accepted', 'Responses', 'Positive', 'MQLs', 'SQLs']],
            body: profiles.map(p => [
                p.profile_name || 'N/A',
                String(p.reachouts_sent || 0),
                String(p.connection_accepted || 0),
                String(p.responses || 0),
                String(p.positive_responses || 0),
                String(p.mqls || 0),
                String(p.qualified_leads || 0),
            ]),
            theme: 'grid',
            headStyles: { fillColor: [0, 119, 181], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { textColor: [50, 50, 50], fontSize: 8 },
            alternateRowStyles: { fillColor: [240, 248, 255] },
            margin: { left: 15, right: 15 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 35 } },
        });
        y = pdf.lastAutoTable.finalY + 10;

        // ── 5. LINKEDIN - TARGETING PER PROFILE ──
        y = addPdfPageCheck(pdf, y, 40);
        y = addPdfSectionTitle(pdf, 'LINKEDIN TARGETING DETAILS', y, pageWidth);

        const targetRows = profiles.map(p => [
            p.profile_name || 'N/A',
            (p.target_industries || []).join(', ') || '-',
            (p.target_company_sizes || []).join(', ') || '-',
            (p.target_designations || []).join(', ') || '-',
        ]);

        autoTable(pdf, {
            startY: y,
            head: [['Profile', 'Target Industries', 'Company Sizes', 'Designations']],
            body: targetRows,
            theme: 'grid',
            headStyles: { fillColor: [0, 119, 181], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { textColor: [50, 50, 50], fontSize: 8 },
            alternateRowStyles: { fillColor: [240, 248, 255] },
            margin: { left: 15, right: 15 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 30 } },
        });
        y = pdf.lastAutoTable.finalY + 12;
    }

    // ── 6. EMAIL PERFORMANCE ──
    y = addPdfPageCheck(pdf, y, 40);
    y = addPdfSectionTitle(pdf, 'EMAIL PERFORMANCE', y, pageWidth);

    autoTable(pdf, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
            ['Emails Sent', String(report.email?.emails_sent || 0)],
            ['Delivered', String(report.email?.delivered || 0)],
            ['Opened', String(report.email?.opened || 0)],
            ['Clicked', String(report.email?.clicked || 0)],
            ['Replies', String(report.email?.replies || 0)],
            ['Positive Replies', String(report.email?.positive_replies || 0)],
            ['MQLs', String(report.email?.mqls || 0)],
            ['Qualified Leads', String(report.email?.qualified_leads || 0)],
        ],
        theme: 'grid',
        headStyles: { fillColor: [234, 67, 53], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { textColor: [50, 50, 50], fontSize: 9 },
        alternateRowStyles: { fillColor: [255, 245, 245] },
        margin: { left: 15, right: 15 },
        tableWidth: pageWidth - 30,
    });
    y = pdf.lastAutoTable.finalY + 10;

    // Email Industry Heatmap
    const emailIndustry = report.email?.industry_heatmap || {};
    if (Object.keys(emailIndustry).length > 0) {
        y = addPdfPageCheck(pdf, y, 30);
        pdf.setTextColor(80, 80, 80);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Email Industry Distribution:', 15, y);
        y += 2;
        autoTable(pdf, {
            startY: y,
            head: [['Industry', 'Count']],
            body: Object.entries(emailIndustry).map(([k, v]) => [k, String(v)]),
            theme: 'grid',
            headStyles: { fillColor: [234, 67, 53], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { textColor: [50, 50, 50], fontSize: 8 },
            margin: { left: 15, right: 15 },
            tableWidth: (pageWidth - 30) / 2,
        });
        y = pdf.lastAutoTable.finalY + 10;
    }

    // ── 7. ADS PERFORMANCE ──
    y = addPdfPageCheck(pdf, y, 40);
    y = addPdfSectionTitle(pdf, 'ADS PERFORMANCE', y, pageWidth);

    autoTable(pdf, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
            ['Accounts Targeted', String(report.ads?.accounts_targeted || 0)],
            ['Impressions', formatNumber(report.ads?.impressions || 0)],
            ['Clicks', String(report.ads?.clicks || 0)],
            ['CTR', `${(report.ads?.ctr || 0).toFixed(2)}%`],
            ['Account Reach', `${(report.ads?.account_reach_percent || 0).toFixed(1)}%`],
            ['Engagement Rate', `${(report.ads?.engagement_rate || 0).toFixed(2)}%`],
            ['Cost per Engaged Account', `$${(report.ads?.cost_per_engaged_account || 0).toFixed(2)}`],
            ['Cost per MQL', `$${(report.ads?.cost_per_mql || 0).toFixed(2)}`],
            ['Influenced Pipeline', `$${formatNumber(report.ads?.influenced_pipeline || 0)}`],
        ],
        theme: 'grid',
        headStyles: { fillColor: [251, 188, 5], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { textColor: [50, 50, 50], fontSize: 9 },
        alternateRowStyles: { fillColor: [255, 252, 235] },
        margin: { left: 15, right: 15 },
        tableWidth: pageWidth - 30,
    });
    y = pdf.lastAutoTable.finalY + 12;

    // ── 8. INBOUND PERFORMANCE ──
    y = addPdfPageCheck(pdf, y, 40);
    y = addPdfSectionTitle(pdf, 'INBOUND PERFORMANCE', y, pageWidth);

    const inboundSummary = [
        ['Total Views', String(report.inbound?.total_views || 0)],
        ['Total Engagement', String(report.inbound?.total_engagement || 0)],
        ['Total Leads', String(report.inbound?.total_leads || 0)],
        ['Content Pieces', String((report.inbound?.contents || []).length)],
    ];
    autoTable(pdf, {
        startY: y,
        head: [['Metric', 'Value']],
        body: inboundSummary,
        theme: 'grid',
        headStyles: { fillColor: [52, 168, 83], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { textColor: [50, 50, 50], fontSize: 9 },
        alternateRowStyles: { fillColor: [240, 255, 245] },
        margin: { left: 15, right: 15 },
        tableWidth: pageWidth - 30,
    });
    y = pdf.lastAutoTable.finalY + 10;

    // Inbound Content details
    const contents = report.inbound?.contents || [];
    if (contents.length > 0) {
        y = addPdfPageCheck(pdf, y, 30);
        autoTable(pdf, {
            startY: y,
            head: [['Title', 'Type', 'Views', 'Likes', 'Shares', 'Leads']],
            body: contents.map(c => [
                c.title || '-',
                c.type || '-',
                String(c.views || 0),
                String(c.likes || 0),
                String(c.shares || 0),
                String(c.leads_generated || 0),
            ]),
            theme: 'grid',
            headStyles: { fillColor: [52, 168, 83], textColor: TEXT_WHITE_RGB, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { textColor: [50, 50, 50], fontSize: 8 },
            margin: { left: 15, right: 15 },
            columnStyles: { 0: { cellWidth: 55 } },
        });
        y = pdf.lastAutoTable.finalY + 12;
    }

    // ── 9. INSIGHTS & OBSERVATIONS ──
    if (report.insights && report.insights.length > 0) {
        y = addPdfPageCheck(pdf, y, 30);
        y = addPdfSectionTitle(pdf, 'INSIGHTS & OBSERVATIONS', y, pageWidth);
        y += 4;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);
        report.insights.forEach((insight) => {
            y = addPdfPageCheck(pdf, y, 12);
            const lines = pdf.splitTextToSize(`  ${insight}`, pageWidth - 34);
            pdf.text(lines, 18, y);
            y += lines.length * 5 + 2;
        });
        y += 5;
    }

    // ── 10. RISKS & BLOCKERS ──
    if (report.risks_blockers && report.risks_blockers.length > 0) {
        y = addPdfPageCheck(pdf, y, 30);
        y = addPdfSectionTitle(pdf, 'RISKS & BLOCKERS', y, pageWidth);
        y += 4;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 50, 50);
        report.risks_blockers.forEach((risk) => {
            y = addPdfPageCheck(pdf, y, 12);
            const lines = pdf.splitTextToSize(`  ${risk}`, pageWidth - 34);
            pdf.text(lines, 18, y);
            y += lines.length * 5 + 2;
        });
        y += 5;
    }

    // ── 11. RECOMMENDATIONS ──
    if (report.recommendations && report.recommendations.length > 0) {
        y = addPdfPageCheck(pdf, y, 30);
        y = addPdfSectionTitle(pdf, 'RECOMMENDATIONS', y, pageWidth);
        y += 4;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(16, 100, 70);
        report.recommendations.forEach((rec) => {
            y = addPdfPageCheck(pdf, y, 12);
            const lines = pdf.splitTextToSize(`  ${rec}`, pageWidth - 34);
            pdf.text(lines, 18, y);
            y += lines.length * 5 + 2;
        });
    }

    // ── Footers ──
    addPdfFooters(pdf);

    pdf.save(`NovantPro-${reportType}-report-${formatDate(report.period_start)}.pdf`);
}


// ========== PPTX EXPORT ==========

const BRAND_RED = 'DC2626';
const DARK_BG = '0A0A0A';
const CARD_BG = '191C24';
const TEXT_WHITE = 'FFFFFF';
const TEXT_MUTED = 'A1A1AA';
const SUCCESS_HEX = '10B981';

function addSlideHeader(slide, title) {
    slide.addText(title, { x: 0.5, y: 0.3, w: 8, h: 0.6, fontSize: 24, fontFace: 'Arial', color: BRAND_RED, bold: true });
    slide.addShape('rect', { x: 0.5, y: 0.85, w: 1.5, h: 0.04, fill: { color: BRAND_RED } });
}

export async function exportToPPTX(report, reportType) {
    const pptx = new PptxGenJS();
    pptx.author = 'NovantPro';
    pptx.title = `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report - ${formatDateRange(report.period_start, report.period_end)}`;
    pptx.layout = 'LAYOUT_WIDE';
    const km = getEffectiveMetrics(report);

    // ── Slide 1: Title ──
    const slide1 = pptx.addSlide();
    slide1.background = { color: DARK_BG };
    slide1.addText('NOVANTPRO', { x: 0.8, y: 1.5, w: 6, h: 1.2, fontSize: 44, fontFace: 'Arial', color: BRAND_RED, bold: true });
    slide1.addText(`${reportType.toUpperCase()} REPORT`, { x: 0.8, y: 2.7, w: 6, h: 0.8, fontSize: 28, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
    slide1.addText(formatDateRange(report.period_start, report.period_end), { x: 0.8, y: 3.5, w: 6, h: 0.5, fontSize: 16, fontFace: 'Arial', color: TEXT_MUTED });
    slide1.addShape('rect', { x: 0.8, y: 4.2, w: 2, h: 0.06, fill: { color: BRAND_RED } });

    // ── Slide 2: Key Metrics ──
    const slide2 = pptx.addSlide();
    slide2.background = { color: DARK_BG };
    addSlideHeader(slide2, 'KEY METRICS');

    const metricsData = [
        ['Target Accounts', km.target_accounts],
        ['Total Contacts', km.total_contacts],
        ['Total Reachouts', km.total_reachouts],
        ['Responses', km.responses],
        ['Positive Responses', km.positive_responses],
        ['Rejects', km.rejects],
        ['Future Leads', km.future_leads],
        ['MQLs', km.mqls],
        ['Qualified Leads', km.qualified_leads],
    ];

    metricsData.forEach(([label, value], i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = 0.5 + col * 4;
        const yPos = 1.2 + row * 1.5;
        slide2.addShape('roundRect', { x, y: yPos, w: 3.6, h: 1.2, fill: { color: CARD_BG }, rectRadius: 0.1 });
        slide2.addText(label.toUpperCase(), { x: x + 0.2, y: yPos + 0.15, w: 3.2, h: 0.35, fontSize: 10, fontFace: 'Arial', color: TEXT_MUTED, bold: true });
        slide2.addText(String(value), { x: x + 0.2, y: yPos + 0.5, w: 3.2, h: 0.5, fontSize: 28, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
    });

    // ── Slide 3: Goal vs Actual ──
    const goals = report.goals || {};
    const hasGoals = goals.target_mqls > 0 || goals.target_qualified_leads > 0 || goals.total_reachouts > 0;
    if (hasGoals) {
        const slideGoal = pptx.addSlide();
        slideGoal.background = { color: DARK_BG };
        addSlideHeader(slideGoal, 'GOAL vs ACTUAL');

        const goalItems = [];
        if (goals.target_mqls > 0) goalItems.push(['MQLs', km.mqls, goals.target_mqls]);
        if (goals.target_qualified_leads > 0) goalItems.push(['SQLs', km.qualified_leads, goals.target_qualified_leads]);
        if (goals.total_reachouts > 0) goalItems.push(['Reachouts', km.total_reachouts, goals.total_reachouts]);
        if (goals.responses > 0) goalItems.push(['Responses', km.responses, goals.responses]);

        goalItems.forEach(([label, actual, goal], i) => {
            const yPos = 1.3 + i * 1.2;
            const pct = goal > 0 ? Math.min((actual / goal) * 100, 100) : 0;
            const achieved = actual >= goal;

            slideGoal.addShape('roundRect', { x: 0.5, y: yPos, w: 12.3, h: 0.95, fill: { color: CARD_BG }, rectRadius: 0.1 });
            slideGoal.addText(label.toUpperCase(), { x: 0.8, y: yPos + 0.1, w: 2, h: 0.35, fontSize: 12, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
            slideGoal.addText(`${actual} / ${goal}`, { x: 3, y: yPos + 0.1, w: 2, h: 0.35, fontSize: 12, fontFace: 'Arial', color: TEXT_MUTED });
            // Progress bar background
            slideGoal.addShape('roundRect', { x: 5.5, y: yPos + 0.3, w: 6, h: 0.35, fill: { color: '27272A' }, rectRadius: 0.05 });
            // Progress bar fill
            slideGoal.addShape('roundRect', { x: 5.5, y: yPos + 0.3, w: Math.max(6 * (pct / 100), 0.1), h: 0.35, fill: { color: achieved ? SUCCESS_HEX : BRAND_RED }, rectRadius: 0.05 });
            slideGoal.addText(`${pct.toFixed(0)}%`, { x: 11.8, y: yPos + 0.1, w: 1, h: 0.35, fontSize: 14, fontFace: 'Arial', color: achieved ? SUCCESS_HEX : BRAND_RED, bold: true, align: 'right' });
        });
    }

    // ── Slide 4: LinkedIn Overview ──
    const slideLI = pptx.addSlide();
    slideLI.background = { color: DARK_BG };
    addSlideHeader(slideLI, 'LINKEDIN PERFORMANCE');

    const liMetrics = [
        ['Reachouts', report.linkedin?.reachouts_sent || 0],
        ['Accepted', report.linkedin?.connection_accepted || 0],
        ['Responses', report.linkedin?.responses || 0],
        ['Positive', report.linkedin?.positive_responses || 0],
        ['MQLs', report.linkedin?.mqls || 0],
        ['SQLs', report.linkedin?.qualified_leads || 0],
    ];
    liMetrics.forEach(([label, value], i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = 0.5 + col * 4;
        const yPos = 1.2 + row * 1.5;
        slideLI.addShape('roundRect', { x, y: yPos, w: 3.6, h: 1.2, fill: { color: CARD_BG }, rectRadius: 0.1 });
        slideLI.addText(label.toUpperCase(), { x: x + 0.2, y: yPos + 0.15, w: 3.2, h: 0.35, fontSize: 10, fontFace: 'Arial', color: '0077B5', bold: true });
        slideLI.addText(String(value), { x: x + 0.2, y: yPos + 0.5, w: 3.2, h: 0.5, fontSize: 28, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
    });

    // ── Slide 5: LinkedIn Profile-wise Breakdown ──
    const profiles = report.linkedin?.profiles || [];
    if (profiles.length > 0) {
        const slideProfiles = pptx.addSlide();
        slideProfiles.background = { color: DARK_BG };
        addSlideHeader(slideProfiles, 'LINKEDIN PROFILE BREAKDOWN');

        const tableRows = [
            [
                { text: 'Profile', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
                { text: 'Reachouts', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
                { text: 'Accepted', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
                { text: 'Responses', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
                { text: 'Positive', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
                { text: 'MQLs', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
                { text: 'SQLs', options: { bold: true, color: TEXT_WHITE, fill: { color: '0077B5' }, fontSize: 9 } },
            ],
        ];
        profiles.forEach(p => {
            tableRows.push([
                { text: p.profile_name || '-', options: { bold: true, color: TEXT_WHITE, fill: { color: CARD_BG }, fontSize: 9 } },
                { text: String(p.reachouts_sent || 0), options: { color: TEXT_MUTED, fill: { color: CARD_BG }, fontSize: 9 } },
                { text: String(p.connection_accepted || 0), options: { color: TEXT_MUTED, fill: { color: CARD_BG }, fontSize: 9 } },
                { text: String(p.responses || 0), options: { color: TEXT_MUTED, fill: { color: CARD_BG }, fontSize: 9 } },
                { text: String(p.positive_responses || 0), options: { color: TEXT_MUTED, fill: { color: CARD_BG }, fontSize: 9 } },
                { text: String(p.mqls || 0), options: { color: TEXT_MUTED, fill: { color: CARD_BG }, fontSize: 9 } },
                { text: String(p.qualified_leads || 0), options: { color: TEXT_MUTED, fill: { color: CARD_BG }, fontSize: 9 } },
            ]);
        });

        slideProfiles.addTable(tableRows, {
            x: 0.5, y: 1.2, w: 12.3,
            border: { pt: 0.5, color: '27272A' },
            colW: [2.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5],
            rowH: 0.45,
        });

        // Targeting details below or on new slide
        const slideTgt = pptx.addSlide();
        slideTgt.background = { color: DARK_BG };
        addSlideHeader(slideTgt, 'LINKEDIN TARGETING DETAILS');

        let yTgt = 1.2;
        profiles.forEach((p, i) => {
            if (yTgt > 6.5) return; // Safety check
            slideTgt.addShape('roundRect', { x: 0.5, y: yTgt, w: 12.3, h: 1.0, fill: { color: CARD_BG }, rectRadius: 0.08 });
            slideTgt.addText(p.profile_name || 'N/A', { x: 0.7, y: yTgt + 0.05, w: 3, h: 0.35, fontSize: 12, fontFace: 'Arial', color: '0077B5', bold: true });
            slideTgt.addText(`Industries: ${(p.target_industries || []).join(', ') || '-'}`, { x: 3.8, y: yTgt + 0.05, w: 9, h: 0.3, fontSize: 9, fontFace: 'Arial', color: TEXT_MUTED });
            slideTgt.addText(`Sizes: ${(p.target_company_sizes || []).join(', ') || '-'}`, { x: 3.8, y: yTgt + 0.35, w: 9, h: 0.3, fontSize: 9, fontFace: 'Arial', color: TEXT_MUTED });
            slideTgt.addText(`Roles: ${(p.target_designations || []).join(', ') || '-'}`, { x: 3.8, y: yTgt + 0.65, w: 9, h: 0.3, fontSize: 9, fontFace: 'Arial', color: TEXT_MUTED });
            yTgt += 1.15;
        });
    }

    // ── Slide 6: Email Performance ──
    const slideEmail = pptx.addSlide();
    slideEmail.background = { color: DARK_BG };
    addSlideHeader(slideEmail, 'EMAIL PERFORMANCE');

    const emailMetrics = [
        ['Sent', report.email?.emails_sent || 0],
        ['Delivered', report.email?.delivered || 0],
        ['Opened', report.email?.opened || 0],
        ['Clicked', report.email?.clicked || 0],
        ['Replies', report.email?.replies || 0],
        ['Positive Replies', report.email?.positive_replies || 0],
        ['MQLs', report.email?.mqls || 0],
        ['Qualified Leads', report.email?.qualified_leads || 0],
    ];
    emailMetrics.forEach(([label, value], i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        const x = 0.5 + col * 3.1;
        const yPos = 1.2 + row * 1.5;
        slideEmail.addShape('roundRect', { x, y: yPos, w: 2.8, h: 1.2, fill: { color: CARD_BG }, rectRadius: 0.1 });
        slideEmail.addText(label.toUpperCase(), { x: x + 0.15, y: yPos + 0.15, w: 2.5, h: 0.3, fontSize: 9, fontFace: 'Arial', color: 'EA4335', bold: true });
        slideEmail.addText(String(value), { x: x + 0.15, y: yPos + 0.5, w: 2.5, h: 0.5, fontSize: 26, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
    });

    // ── Slide 7: Ads Performance ──
    const slideAds = pptx.addSlide();
    slideAds.background = { color: DARK_BG };
    addSlideHeader(slideAds, 'ADS PERFORMANCE');

    const adsMetrics = [
        ['Accts Targeted', report.ads?.accounts_targeted || 0],
        ['Impressions', formatNumber(report.ads?.impressions || 0)],
        ['Clicks', report.ads?.clicks || 0],
        ['CTR', `${(report.ads?.ctr || 0).toFixed(2)}%`],
        ['Acct Reach', `${(report.ads?.account_reach_percent || 0).toFixed(1)}%`],
        ['Engagement', `${(report.ads?.engagement_rate || 0).toFixed(2)}%`],
        ['Cost/MQL', `$${(report.ads?.cost_per_mql || 0).toFixed(0)}`],
        ['Pipeline', `$${formatNumber(report.ads?.influenced_pipeline || 0)}`],
    ];
    adsMetrics.forEach(([label, value], i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        const x = 0.5 + col * 3.1;
        const yPos = 1.2 + row * 1.5;
        slideAds.addShape('roundRect', { x, y: yPos, w: 2.8, h: 1.2, fill: { color: CARD_BG }, rectRadius: 0.1 });
        slideAds.addText(label.toUpperCase(), { x: x + 0.15, y: yPos + 0.15, w: 2.5, h: 0.3, fontSize: 9, fontFace: 'Arial', color: 'FBBC05', bold: true });
        slideAds.addText(String(value), { x: x + 0.15, y: yPos + 0.5, w: 2.5, h: 0.5, fontSize: 26, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
    });

    // ── Slide 8: Inbound ──
    const slideInbound = pptx.addSlide();
    slideInbound.background = { color: DARK_BG };
    addSlideHeader(slideInbound, 'INBOUND PERFORMANCE');

    const inboundMetrics = [
        ['Views', report.inbound?.total_views || 0],
        ['Engagement', report.inbound?.total_engagement || 0],
        ['Leads', report.inbound?.total_leads || 0],
        ['Content', (report.inbound?.contents || []).length],
    ];
    inboundMetrics.forEach(([label, value], i) => {
        const x = 0.5 + i * 3.1;
        slideInbound.addShape('roundRect', { x, y: 1.2, w: 2.8, h: 1.2, fill: { color: CARD_BG }, rectRadius: 0.1 });
        slideInbound.addText(label.toUpperCase(), { x: x + 0.15, y: 1.35, w: 2.5, h: 0.3, fontSize: 10, fontFace: 'Arial', color: '34A853', bold: true });
        slideInbound.addText(String(value), { x: x + 0.15, y: 1.7, w: 2.5, h: 0.5, fontSize: 28, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
    });

    // Content list
    const contents = report.inbound?.contents || [];
    if (contents.length > 0) {
        let yC = 2.8;
        contents.forEach((c) => {
            if (yC > 6.8) return;
            slideInbound.addShape('roundRect', { x: 0.5, y: yC, w: 12.3, h: 0.7, fill: { color: CARD_BG }, rectRadius: 0.06 });
            slideInbound.addText(c.title || '-', { x: 0.7, y: yC + 0.08, w: 5, h: 0.25, fontSize: 10, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
            slideInbound.addText(`${c.type || '-'}  |  Views: ${c.views || 0}  |  Likes: ${c.likes || 0}  |  Leads: ${c.leads_generated || 0}`, {
                x: 0.7, y: yC + 0.35, w: 11, h: 0.25, fontSize: 8, fontFace: 'Arial', color: TEXT_MUTED
            });
            yC += 0.85;
        });
    }

    // ── Slide 9: Insights & Recommendations ──
    if ((report.insights?.length > 0) || (report.recommendations?.length > 0) || (report.risks_blockers?.length > 0)) {
        const slideIR = pptx.addSlide();
        slideIR.background = { color: DARK_BG };
        addSlideHeader(slideIR, 'INSIGHTS & RECOMMENDATIONS');

        let yPos = 1.2;

        if (report.insights?.length > 0) {
            slideIR.addText('INSIGHTS & OBSERVATIONS', { x: 0.5, y: yPos, w: 6, h: 0.35, fontSize: 13, fontFace: 'Arial', color: TEXT_WHITE, bold: true });
            yPos += 0.45;
            report.insights.forEach((insight) => {
                if (yPos > 7) return;
                slideIR.addText(`  ${insight}`, { x: 0.7, y: yPos, w: 11.5, h: 0.35, fontSize: 11, fontFace: 'Arial', color: TEXT_MUTED });
                yPos += 0.4;
            });
            yPos += 0.2;
        }

        if (report.risks_blockers?.length > 0) {
            slideIR.addText('RISKS & BLOCKERS', { x: 0.5, y: yPos, w: 6, h: 0.35, fontSize: 13, fontFace: 'Arial', color: 'EF4444', bold: true });
            yPos += 0.45;
            report.risks_blockers.forEach((risk) => {
                if (yPos > 7) return;
                slideIR.addText(`  ${risk}`, { x: 0.7, y: yPos, w: 11.5, h: 0.35, fontSize: 11, fontFace: 'Arial', color: 'F59E0B' });
                yPos += 0.4;
            });
            yPos += 0.2;
        }

        if (report.recommendations?.length > 0) {
            if (yPos > 5.5) {
                // New slide if running out of space
                const slideRec = pptx.addSlide();
                slideRec.background = { color: DARK_BG };
                addSlideHeader(slideRec, 'RECOMMENDATIONS');
                yPos = 1.2;
                report.recommendations.forEach((rec) => {
                    if (yPos > 7) return;
                    slideRec.addText(`  ${rec}`, { x: 0.7, y: yPos, w: 11.5, h: 0.35, fontSize: 11, fontFace: 'Arial', color: SUCCESS_HEX });
                    yPos += 0.4;
                });
            } else {
                slideIR.addText('RECOMMENDATIONS', { x: 0.5, y: yPos, w: 6, h: 0.35, fontSize: 13, fontFace: 'Arial', color: SUCCESS_HEX, bold: true });
                yPos += 0.45;
                report.recommendations.forEach((rec) => {
                    if (yPos > 7) return;
                    slideIR.addText(`  ${rec}`, { x: 0.7, y: yPos, w: 11.5, h: 0.35, fontSize: 11, fontFace: 'Arial', color: SUCCESS_HEX });
                    yPos += 0.4;
                });
            }
        }
    }

    await pptx.writeFile({ fileName: `NovantPro-${reportType}-report-${formatDate(report.period_start)}.pptx` });
}
